import lodash from 'lodash';
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { CustomField } from '@/api/openapi';
import { useWait } from '@/composable/vue-wait';
import { key } from '@/store';
import { useVuexCallTargets } from './call-target';
import { isCustomFieldKey, TFilterParams, useVuexFilterParams } from './lead-filter';

type TFilterKeyType = 'typeArray' | 'typeBoolean' | 'typeString' | 'typeNumber';
const getInitialValue = (filterType: TFilterKeyType): (string | number)[] | null | string => {
  switch (filterType) {
    case 'typeArray':
      return [];
    case 'typeBoolean':
      return null;
    case 'typeString':
      return '';
    case 'typeNumber':
    // TODO: そのうち文字列型で扱うのをやめたい
      return ['null', 'null', 'false'];
    default:
      console.error('invalid filterType. filterType = ', filterType);
      return '';
  }
};

// どの key がどのタイプのフィルタを持つか定義する
const filterKeyTypes: Record<string, TFilterKeyType> = {
  'companyItemsCompanyName': 'typeString',
  'companyItemsCompanyTel': 'typeString',
  'companyItemsCompanyAddress': 'typeString',
  'companyItemsBusinessCategory': 'typeArray',
  'companyItemsNumberOfEmployees': 'typeArray',
  'targetItemsDepartment': 'typeArray',
  'targetItemsPosition': 'typeArray',
  'targetItemsEmail': 'typeBoolean',
  'targetItemsEmailText': 'typeString',
  'targetItemsName': 'typeString',
  'targetItemsTel': 'typeString',
  'callResults': 'typeArray',
  'lastCallCalledAt': 'typeArray',
  'lastCallDuration': 'typeArray',
  'numberOfCall': 'typeArray',
  'opportunitiesPresent': 'typeBoolean',
  'opportunitiesCreatedAt': 'typeArray',
  'opportunitiesStartedAt': 'typeArray',
  'unsubscriptionPresent': 'typeBoolean',
  'bulkMails': 'typeArray',
  'bulkMailEvents': 'typeString',
  'directMailEvents': 'typeString',
  'lastLeadStage': 'typeArray',
  'lastLeadStageStayDays': 'typeArray',
  'assignedOperator': 'typeArray',
  'leadSources': 'typeArray',
  'leadSourcesRegisteredAt': 'typeArray',
  'freeTags': 'typeArray',
  'nextActionsActionType': 'typeArray',
  'nextActionsPriority': 'typeArray',
  'nextActionsReservedAt': 'typeArray',
  'nextActionsDoneAt': 'typeArray',
  'nextActionsPresent': 'typeBoolean',
  'nextActionsCompletedPresent': 'typeBoolean',
  'nextActionsOwner': 'typeArray',
  'nextActionsOwnerPresent': 'typeBoolean',
  'nextActionsDoneBy': 'typeArray',
  'callTargetsCreatedAt': 'typeArray',
  'sfRelatedLeadStatus': 'typeArray',
  'duplicateEmail': 'typeString',
  'leadDuplicatePresent': 'typeBoolean',
  'sfCampaign': 'typeArray',
};

// 複数の key を内包する key を定義する
const multipleFilterKey: Record<string, string[]> = {
  'bulkMails': ['bulkMails', 'bulkMailEvents'],
  'directMails': ['directMailEvents'],
  'leadSources': ['leadSources', 'leadSourcesRegisteredAt'],
};

const findNormalFilterType = (filterKey: string): TFilterKeyType => {
  if (filterKeyTypes[filterKey]) return filterKeyTypes[filterKey];
  console.error(`couldn't not found type. key =`, filterKey);
  return 'typeString';
};

function getMultipleFilterKey(filterKey: string): string[];
function getMultipleFilterKey(): Record<string, string[]>;
function getMultipleFilterKey(filterKey?: string): string[] | Record<string, string[]> {
  return filterKey ? multipleFilterKey[filterKey] : multipleFilterKey;
}

/**
 * リードリストのフィルタ操作ロジック。
 * 旧 mixin `@/mixins/filter_service` の置き換え。
 */
const useFilterService = () => {
  const store = useStore(key);
  const route = useRoute();
  const router = useRouter();
  const { doActionWithWait } = useWait();
  const { setLeadListFilterParams } = useVuexFilterParams();
  const { fetchCallTargets } = useVuexCallTargets();

  const customFields = computed<CustomField[]>(() => store.getters['user/customFields']);
  const leadListFilterParams = computed<TFilterParams>(() => store.getters['userUi/leadListFilterParams']);
  const leadListPageParams = computed(() => store.getters['userUi/leadListPageParams']);

  const findCustomFilterType = (fieldKey: string): TFilterKeyType => {
    const customField = customFields.value.find((cf) => cf.fieldKey === fieldKey);
    if (customField == null) {
      console.error(`invalid fieldKey. fieldKey = `, fieldKey);
      return 'typeString';
    }
    switch (customField.dataType) {
      case 'text':
        return 'typeString';
      case 'datetime':
        return 'typeArray';
      case 'option':
        return 'typeArray';
      case 'number':
        return 'typeNumber';
      case 'multi_option':
        return 'typeArray';
      case 'boolean':
        return 'typeBoolean';
      default:
        console.error(`invalid dataType. dataType = `, customField.dataType);
        return 'typeString';
    }
  };

  const getFilterType = (filterKey: string): TFilterKeyType => {
    return isCustomFieldKey(filterKey)
      ? findCustomFilterType(filterKey)
      : findNormalFilterType(filterKey);
  };

  const addFilterItem = (shouldAddKey: string): void => {
    const newLeadListFilterParams = lodash.cloneDeep(
      leadListFilterParams.value,
    );
    const keyType = getFilterType(shouldAddKey);
    const param = {
      inc: { or: getInitialValue(keyType) },
    };
    if (isCustomFieldKey(shouldAddKey)) {
      if (Object.prototype.hasOwnProperty.call(newLeadListFilterParams, 'customFieldItems')) {
        // 既に customFieldItems 自体は存在する場合
        newLeadListFilterParams.customFieldItems[shouldAddKey] = param;
      } else {
        // customFieldItems 自体が存在しない場合は初期化して定義する
        newLeadListFilterParams.customFieldItems = {
          [shouldAddKey]: param,
        };
      }
    } else {
      newLeadListFilterParams[shouldAddKey] = param;
    }

    setLeadListFilterParams(newLeadListFilterParams);
  };

  const addMultipleFilterItem = (shouldAddKeys: string[]): void => {
    shouldAddKeys.forEach((filterKey) => {
      addFilterItem(filterKey);
    });
  };

  const setLeadListPageParams = (pageParams: unknown) => doActionWithWait('setLeadListPageParamsWait', async () => {
    await store.dispatch('userUi/setLeadListPageParamsAction', pageParams);
  });
  const setFilterControlPanelVisible = (visible: boolean) => doActionWithWait('setFilterControlPanelVisibleWait', async () => {
    await store.dispatch('userUi/setFilterControlPanelVisibleAction', visible);
  });

  const setFilterAndMoveLeadList = (filterParams: TFilterParams = {}): void => {
    if (route.name !== 'LeadList') {
      router.push({ name: 'LeadList' });
    }
    setLeadListFilterParams(filterParams);
    // 現在の表示数設定で1ページ目を表示する
    setLeadListPageParams(Object.assign(lodash.cloneDeep(leadListPageParams.value), { currentPage: 1 }));
    setFilterControlPanelVisible(true);
    fetchCallTargets();
  };

  return {
    getFilterType,
    addFilterItem,
    addMultipleFilterItem,
    getMultipleFilterKey,
    setFilterAndMoveLeadList,
  };
};

export type {
  TFilterKeyType,
};
export {
  getInitialValue,
  filterKeyTypes,
  multipleFilterKey,
  useFilterService,
};
